import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { getAllUsersNoPagination, updateUserContact } from '../../services/userService';
import { useAuth } from '../../context/AuthContext';
import { useEventBus } from '../../hooks/useEventBus';
import { 
  formatPhoneForDisplay, 
  formatPhoneForStorage, 
  validatePhoneNumber, 
  handlePhoneKeyPress 
} from '../../utils/phoneUtils';

const Directory = () => {
  const { user: currentUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [sortConfig, setSortConfig] = useState({
    direction: 'asc',
    field: 'username'
  });
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedUserForMobile, setSelectedUserForMobile] = useState(''); // For mobile dropdown
  const [formData, setFormData] = useState({
    workPhone: '',
    homePhone: '',
    mobilePhone: '',
    emergencyContact: '',
    emergencyPhone1: '',
    emergencyPhone2: '',
  });

  const canEdit = currentUser?.role === 'admin' || currentUser?.role === 'team_lead';

  // Memoize fetchUsers to prevent unnecessary re-renders
  const fetchUsers = React.useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await getAllUsersNoPagination();
      // Filter out inactive users and admin users
      const filteredUsers = data.filter(user => 
        user.isActive && user.role !== 'admin'
      );
      setUsers(filteredUsers);
    } catch (err) {
      setError(err.message || 'Failed to fetch users');
      console.error('Error fetching users:', err);
    } finally {
      setLoading(false);
    }
  }, []); // No dependencies needed for fetchUsers

  // Subscribe to contact info updates
  useEventBus('contactInfoUpdated', fetchUsers);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleOpenDialog = (user) => {
    setError(null);
    setSelectedUser(user);
    setFormData({
      workPhone: user.workPhone || '',
      homePhone: user.homePhone || '',
      mobilePhone: user.mobilePhone || '',
      emergencyContact: user.emergencyContact || '',
      emergencyPhone1: user.emergencyPhone1 || '',
      emergencyPhone2: user.emergencyPhone2 || '',
    });
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedUser(null);
    setError(null);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handlePhoneChange = (e) => {
    const { name, value } = e.target;
    const formatted = formatPhoneForDisplay(value);
    setFormData((prev) => ({
      ...prev,
      [name]: formatted,
    }));
  };

  const handleSubmit = async () => {
    try {
      setError(null);
      await updateUserContact(selectedUser._id, formData);
      handleCloseDialog();
      await fetchUsers();
    } catch (err) {
      console.error('Error in form submission:', err);
      setError(err.message || 'Failed to update contact information');
    }
  };

  const handleMobileUserSelect = (event) => {
    const selectedValue = event.target.value;
    setSelectedUserForMobile(selectedValue);
  };

  // Memoize sorted users to prevent index shifting
  const sortedUsers = React.useMemo(() => {
    return [...users].sort((a, b) => {
      const nameA = `${a.firstName} ${a.lastName}`;
      const nameB = `${b.firstName} ${b.lastName}`;
      return nameA.localeCompare(nameB);
    });
  }, [users]);

  const getSelectedUserData = () => {
    if (!selectedUserForMobile) return null;
    return users.find(user => user._id === selectedUserForMobile);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {error && (
        <Typography color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      {isMobile ? (
        // Mobile layout: dropdown + 2-column table
        <>
          {/* Name dropdown - only render when users are loaded */}
          {users.length > 0 && (
            <FormControl fullWidth sx={{ mb: 3 }} size="small">
              <InputLabel id="user-select-label">Select Name</InputLabel>
              <Select
                labelId="user-select-label"
                id="user-select"
                value={selectedUserForMobile}
                label="Select Name"
                onChange={handleMobileUserSelect}
              >
                {sortedUsers.map((user, index) => (
                  <MenuItem key={user._id} value={user._id}>
                    {`${user.firstName} ${user.lastName}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}

          {/* 2-column table for selected user */}
          {getSelectedUserData() && (
            <TableContainer component={Paper}>
              <Table size="small">
                <TableBody>
                  {/* Email row */}
                  <TableRow>
                    <TableCell sx={{ 
                      fontWeight: 'bold', 
                      width: '40%',
                      backgroundColor: '#e0e0e0',
                      borderRight: '1px solid #bdbdbd',
                      borderBottom: '1px solid #bdbdbd'
                    }}>
                      Email
                    </TableCell>
                    <TableCell sx={{ 
                      borderBottom: '1px solid #e0e0e0'
                    }}>
                      {getSelectedUserData().email}
                    </TableCell>
                  </TableRow>
                  
                  {/* Work Phone row */}
                  <TableRow>
                    <TableCell sx={{ 
                      fontWeight: 'bold',
                      backgroundColor: '#e0e0e0',
                      borderRight: '1px solid #bdbdbd',
                      borderBottom: '1px solid #bdbdbd'
                    }}>
                      Work Phone
                    </TableCell>
                    <TableCell sx={{ 
                      borderBottom: '1px solid #e0e0e0'
                    }}>
                      {getSelectedUserData().workPhone || '-'}
                    </TableCell>
                  </TableRow>
                  
                  {/* Home Phone row */}
                  <TableRow>
                    <TableCell sx={{ 
                      fontWeight: 'bold',
                      backgroundColor: '#e0e0e0',
                      borderRight: '1px solid #bdbdbd',
                      borderBottom: '1px solid #bdbdbd'
                    }}>
                      Home Phone
                    </TableCell>
                    <TableCell sx={{ 
                      borderBottom: '1px solid #e0e0e0'
                    }}>
                      {getSelectedUserData().homePhone || '-'}
                    </TableCell>
                  </TableRow>
                  
                  {/* Mobile Phone row */}
                  <TableRow>
                    <TableCell sx={{ 
                      fontWeight: 'bold',
                      backgroundColor: '#e0e0e0',
                      borderRight: '1px solid #bdbdbd',
                      borderBottom: '1px solid #bdbdbd'
                    }}>
                      Mobile Phone
                    </TableCell>
                    <TableCell sx={{ 
                      borderBottom: '1px solid #e0e0e0'
                    }}>
                      {getSelectedUserData().mobilePhone || '-'}
                    </TableCell>
                  </TableRow>
                  
                  {/* Emergency Contact row */}
                  <TableRow>
                    <TableCell sx={{ 
                      fontWeight: 'bold',
                      backgroundColor: '#e0e0e0',
                      borderRight: '1px solid #bdbdbd',
                      borderBottom: '1px solid #bdbdbd'
                    }}>
                      Emergency Contact
                    </TableCell>
                    <TableCell sx={{ 
                      borderBottom: '1px solid #e0e0e0'
                    }}>
                      {getSelectedUserData().emergencyContact || '-'}
                    </TableCell>
                  </TableRow>
                  
                  {/* Emergency Phone 1 row */}
                  <TableRow>
                    <TableCell sx={{ 
                      fontWeight: 'bold',
                      backgroundColor: '#e0e0e0',
                      borderRight: '1px solid #bdbdbd',
                      borderBottom: '1px solid #bdbdbd'
                    }}>
                      Emergency Phone 1
                    </TableCell>
                    <TableCell sx={{ 
                      borderBottom: '1px solid #e0e0e0'
                    }}>
                      {getSelectedUserData().emergencyPhone1 || '-'}
                    </TableCell>
                  </TableRow>
                  
                  {/* Emergency Phone 2 row */}
                  <TableRow>
                    <TableCell sx={{ 
                      fontWeight: 'bold',
                      backgroundColor: '#e0e0e0',
                      borderRight: '1px solid #bdbdbd',
                      borderBottom: '1px solid #bdbdbd'
                    }}>
                      Emergency Phone 2
                    </TableCell>
                    <TableCell sx={{ 
                      borderBottom: '1px solid #e0e0e0'
                    }}>
                      {getSelectedUserData().emergencyPhone2 || '-'}
                    </TableCell>
                  </TableRow>
                  
                  {/* Actions row - only show if user can edit */}
                  {canEdit && (
                    <TableRow>
                      <TableCell 
                        colSpan={2}
                        sx={{ 
                          backgroundColor: '#e3f2fd',
                          textAlign: 'center',
                          padding: '12px',
                          borderTop: '1px solid #e0e0e0'
                        }}
                      >
                        <IconButton
                          onClick={() => handleOpenDialog(getSelectedUserData())}
                          size="small"
                        >
                          <EditIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      ) : (
        // Desktop layout: original horizontal table
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell 
                  onClick={() => {
                    setSortConfig(prev => ({
                      direction: prev.field === 'username' && prev.direction === 'asc' ? 'desc' : 'asc',
                      field: 'username'
                    }));
                  }}
                  sx={{ 
                    cursor: 'pointer',
                    '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
                    userSelect: 'none'
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Username
                    {sortConfig.field === 'username' && (
                      <span style={{ marginLeft: '4px' }}>
                        {sortConfig.direction === 'asc' ? '▲' : '▼'}
                      </span>
                    )}
                  </Box>
                </TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Work Phone</TableCell>
                <TableCell>Home Phone</TableCell>
                <TableCell>Mobile Phone</TableCell>
                <TableCell>Emergency Contact</TableCell>
                <TableCell>Emergency Phone 1</TableCell>
                <TableCell>Emergency Phone 2</TableCell>
                {canEdit && <TableCell>Action</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {[...users]
                .sort((a, b) => {
                  if (sortConfig.field === 'username') {
                    return sortConfig.direction === 'asc'
                      ? a.username.localeCompare(b.username)
                      : b.username.localeCompare(a.username);
                  }
                  return 0;
                })
                .map((user) => (
                <TableRow key={user._id}>
                  <TableCell>{user.username}</TableCell>
                  <TableCell>{`${user.firstName} ${user.lastName}`}</TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{user.workPhone || '-'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{user.homePhone || '-'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{user.mobilePhone || '-'}</TableCell>
                  <TableCell>{user.emergencyContact || '-'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{user.emergencyPhone1 || '-'}</TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>{user.emergencyPhone2 || '-'}</TableCell>
                  {canEdit && (
                    <TableCell>
                      <IconButton onClick={() => handleOpenDialog(user)} size="small">
                        <EditIcon />
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Dialog 
        open={openDialog} 
        onClose={handleCloseDialog} 
        maxWidth="sm" 
        fullWidth
        sx={{}}
      >
        <DialogTitle>
          Edit Contact Information - {selectedUser?.firstName} {selectedUser?.lastName}
        </DialogTitle>
        <DialogContent>
          {error && (
            <Typography color="error" sx={{ mb: 2 }}>
              {error}
            </Typography>
          )}
          <Box sx={{ mt: 2, display: 'grid', gap: 2 }}>
            <TextField
              name="workPhone"
              label="Work Phone"
              type="tel"
              fullWidth
              value={formData.workPhone}
              onChange={handlePhoneChange}
              onKeyDown={handlePhoneKeyPress}
              autoComplete="off"
              inputMode="tel"
              pattern="[0-9-]+"
              inputProps={{
                'data-lpignore': 'true',
                'data-1p-ignore': 'true',
                'data-form-type': 'other'
              }}
            />
            <TextField
              name="homePhone"
              label="Home Phone"
              type="tel"
              fullWidth
              value={formData.homePhone}
              onChange={handlePhoneChange}
              onKeyDown={handlePhoneKeyPress}
              autoComplete="off"
              inputMode="tel"
              pattern="[0-9-]+"
              inputProps={{
                'data-lpignore': 'true',
                'data-1p-ignore': 'true',
                'data-form-type': 'other'
              }}
            />
            <TextField
              name="mobilePhone"
              label="Mobile Phone"
              type="tel"
              fullWidth
              value={formData.mobilePhone}
              onChange={handlePhoneChange}
              onKeyDown={handlePhoneKeyPress}
              autoComplete="off"
              inputMode="tel"
              pattern="[0-9-]+"
              inputProps={{
                'data-lpignore': 'true',
                'data-1p-ignore': 'true',
                'data-form-type': 'other'
              }}
            />
            <TextField
              name="emergencyContact"
              label="Emergency Contact"
              fullWidth
              value={formData.emergencyContact}
              onChange={handleInputChange}
              autoComplete="off"
              inputProps={{
                'data-lpignore': 'true',
                'data-1p-ignore': 'true',
                'data-form-type': 'other'
              }}
            />
            <TextField
              name="emergencyPhone1"
              label="Emergency Phone 1"
              type="tel"
              fullWidth
              value={formData.emergencyPhone1}
              onChange={handlePhoneChange}
              onKeyDown={handlePhoneKeyPress}
              autoComplete="off"
              inputMode="tel"
              pattern="[0-9-]+"
              inputProps={{
                'data-lpignore': 'true',
                'data-1p-ignore': 'true',
                'data-form-type': 'other'
              }}
            />
            <TextField
              name="emergencyPhone2"
              label="Emergency Phone 2"
              type="tel"
              fullWidth
              value={formData.emergencyPhone2}
              onChange={handlePhoneChange}
              onKeyDown={handlePhoneKeyPress}
              autoComplete="off"
              inputMode="tel"
              pattern="[0-9-]+"
              inputProps={{
                'data-lpignore': 'true',
                'data-1p-ignore': 'true',
                'data-form-type': 'other'
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 0, justifyContent: 'flex-end' }}>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Directory;
